/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';

import { jsx, cssMap } from '@compiled/react';
import { bind } from 'bind-event-listener';
import { useIntl } from 'react-intl';

import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages/ai-suggestions';
import { findOverflowScrollParent } from '@atlaskit/editor-common/Popup/utils';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import TextReviewIcon from '@atlaskit/icon-lab/core/text-review';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Anchor } from '@atlaskit/primitives/compiled';
import { AgentAvatar } from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import { token } from '@atlaskit/tokens';
// Reuse Rovo inline chat's exact error copy and documentation links.
import errorViewMessages from '@atlassian/conversation-assistant-chat-prompt-input/ui/inline-chat-prompt/views/error-view/messages';
import { useUIStore } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { RovoThinkingBarWrapper } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingBarWrapper';
import {
	RovoThinkingMinimization,
	type RovoThinkingMinimizationHandle,
} from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingMinimization';
import { RovoThinkingReadyTransition } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingReadyTransition';

import type { AiSuggestionsPlugin } from '../../../aiSuggestionsPluginType';
import { useContentReviewerAgentResolver } from '../../hooks/useContentReviewerAgentResolver';
import { useSkill } from '../../hooks/useSkill';
import { useSyncedThinkingStatus } from '../../hooks/useSyncedThinkingStatus';
import { fireSuggestionCancelledAnalytics } from '../../utils/analytics';
import { toReviewingAgent } from '../../utils/reviewingAgent';
import {
	getReviewCompleteMessage,
	getReviewingContentWithAgentMessage,
	getThinkingRotatingMessages,
} from './thinkingMessages';

type ThinkingContentComponentProps = {
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined;
	editorView?: EditorView;
};

const AUTO_CLOSE_DELAY_MS = 3000;

const styles = cssMap({
	container: {
		position: 'fixed',
		bottom: token('space.300'),
		minWidth: 400,
		zIndex: 510, // same as akEditorFloatingDialogZIndex
		left: '50%',
		transform: 'translateX(-50%)',
	},
	resizableContainer: {
		maxWidth: `calc(100vw - ${token('space.600')})`,
	},
	hidden: {
		display: 'none',
	},
});

export function ThinkingContentComponent({
	api,
	editorView,
}: ThinkingContentComponentProps): JSX.Element | null {
	const { formatMessage } = useIntl();
	const thinkingStatus = useSyncedThinkingStatus(api);
	const minimizationRef = useRef<RovoThinkingMinimizationHandle>(null);
	const isRequestingInlineCommentCloseRef = useRef(false);
	/**
	 * We use this to detect when to hide / show the thinking component
	 */
	const { mounted: isRovoMounted } = useUIStore();

	const {
		firstSuggestionId,
		isHidden,
		thinkingStartedAt,
		thinkingProgressTarget,
		thinkingError,
		reviewResults,
		agent,
	} = useSharedPluginStateWithSelector(api, ['aiSuggestions', 'editorViewMode'], (state) => ({
		firstSuggestionId: state?.aiSuggestionsState?.orderedSuggestionIds?.[0],
		reviewResults: state?.aiSuggestionsState?.reviewResults,
		// View mode is a hard override: never show the suggestions UI while read-only.
		isHidden:
			(state?.aiSuggestionsState?.isHidden ?? false) || state?.editorViewModeState?.mode === 'view',
		// Shared thinking-session anchor so this bar and the sidebar bar keep
		// their progress/rotation in sync across mount/unmount.
		thinkingStartedAt: state?.aiSuggestionsState?.thinkingStartedAt ?? null,
		thinkingProgressTarget: state?.aiSuggestionsState?.thinkingProgressTarget ?? null,
		thinkingError: state?.aiSuggestionsState?.thinkingError ?? null,
		// Resolved once per editor and shared, so this bar and the product's own
		// loading states name the same agent.
		agent: state?.aiSuggestionsState?.reviewingAgent ?? null,
	}));

	useSkill({
		api,
		thinkingStatus,
		isRovoMounted,
	});

	const { resolveAgent } = useContentReviewerAgentResolver({ api });

	// Resolve the reviewing agent once a thinking session actually starts — this
	// component is always mounted, so resolving on mount would provision an OOTB
	// agent before the user has asked for a review. Gated on the same flag that
	// determines whether an agent actually handles the review. The agent store
	// caches the lookup, so later renders of this effect (e.g. a retry) don't
	// refetch once it has resolved.
	useEffect(() => {
		if (agent || thinkingStatus !== 'thinking' || !fg('content_reviewer_agent')) {
			return;
		}

		let isActive = true;
		const abortController = new AbortController();

		void resolveAgent({ signal: abortController.signal }).then((resolved) => {
			const reviewingAgent = toReviewingAgent(resolved);
			if (isActive && reviewingAgent) {
				api?.aiSuggestions?.actions.setReviewingAgent(reviewingAgent);
			}
		});

		return () => {
			isActive = false;
			abortController.abort();
		};
	}, [agent, api, resolveAgent, thinkingStatus]);

	// Collapse the thinking bar into the pill as soon as the user scrolls the
	// editor, so it stays out of the way while they read/navigate. Only wired up
	// while actively thinking (the pill is only shown then); the listener binds
	// to the editor's real overflow-scroll ancestor, falling back to the window
	// when the page itself scrolls.
	useEffect(() => {
		if (thinkingStatus !== 'thinking' || !editorView) {
			return;
		}
		const scrollTarget: HTMLElement | Window = findOverflowScrollParent(editorView.dom) || window;
		return bind(scrollTarget, {
			type: 'scroll',
			listener: () => {
				minimizationRef.current?.minimize();
			},
			options: { passive: true },
		});
	}, [editorView, thinkingStatus]);

	const handleStop = useCallback(() => {
		fireSuggestionCancelledAnalytics(api?.analytics?.actions, 'statusBar');
		api?.aiSuggestions?.actions.setThinkingState('idle');
	}, [api]);

	const onViewSuggestions = useCallback(() => {
		const viewSuggestions = async (): Promise<void> => {
			if (
				UNSAFE_expValNoExposure('confluence_suggested_edits_inline_comment_fix', 'isEnabled', false)
			) {
				if (isRequestingInlineCommentCloseRef.current) {
					return;
				}

				isRequestingInlineCommentCloseRef.current = true;

				const canCloseComment = await (
					api?.annotation?.actions.requestCloseInlineComment?.() ?? Promise.resolve(true)
				).finally(() => {
					isRequestingInlineCommentCloseRef.current = false;
				});

				if (!canCloseComment) {
					return;
				}
			}

			api?.aiSuggestions?.actions.setThinkingState('idle');
			api?.aiSuggestions?.actions.selectSuggestion(
				firstSuggestionId ?? null,
				firstSuggestionId ? { interactionPoint: 'statusBar' } : undefined,
			);
		};

		void viewSuggestions();
	}, [api, firstSuggestionId]);

	const customRotatingMessages = useMemo(
		() => getThinkingRotatingMessages(formatMessage),
		[formatMessage],
	);

	// Once the reviewing agent resolves, override the first status to name it and
	// show its avatar in place of the usual thinking icon.
	const firstMessage = useMemo(() => {
		if (!agent) {
			return undefined;
		}

		return {
			text: getReviewingContentWithAgentMessage(formatMessage, agent.name),
			icon: (
				<AgentAvatar
					agentId={agent.id}
					agentNamedId={agent.namedId}
					agentIdentityAccountId={agent.identityAccountId}
					size="small"
					showBorder={false}
				/>
			),
		};
	}, [agent, formatMessage]);

	// When the auto-close timer fires, only dismiss (return to idle) if there
	// are no suggestions — matching the legacy ThinkingTransitionWrapper
	// behaviour where the complete bar stayed visible until the user clicked
	// "View Suggestions" or "Close".
	const handleClose = useCallback(() => {
		api?.aiSuggestions?.actions.setThinkingState('idle');
	}, [api]);

	const handleTryAgain = useCallback(() => {
		api?.aiSuggestions?.actions.setThinkingState('thinking', { isRetry: true });
	}, [api]);

	const isError = thinkingStatus === 'error';
	const isReady = thinkingStatus === 'complete';
	const hasSuggestions = !!firstSuggestionId;

	const readyText = reviewResults
		? getReviewCompleteMessage(formatMessage, reviewResults, agent?.name)
		: hasSuggestions
			? formatMessage(
					fg('rovo_ready_bar_ui_update')
						? aiSuggestionsMessages.thinkingCompleteReviewCompleteUpdated
						: aiSuggestionsMessages.thinkingCompleteReviewComplete,
				)
			: formatMessage(
					fg('rovo_ready_bar_ui_update')
						? aiSuggestionsMessages.thinkingCompleteNoSuggestionsUpdated
						: aiSuggestionsMessages.thinkingCompleteNoSuggestions,
				);

	const thinkingBarProps = useMemo(
		() => ({
			onStop: handleStop,
			showProgressBar: true,
			useRotatingTitle: true,
			customRotatingMessages,
			startedAt: thinkingStartedAt,
			progressTarget: thinkingProgressTarget,
			firstMessage,
		}),
		[handleStop, customRotatingMessages, thinkingStartedAt, thinkingProgressTarget, firstMessage],
	);

	const thinkingReadyProps = useMemo(
		() => ({
			onClose: handleClose,
			onView: hasSuggestions ? onViewSuggestions : undefined,
			readyText,
			successIcon: <TextReviewIcon label="" size="medium" />,
		}),
		[handleClose, hasSuggestions, onViewSuggestions, readyText],
	);

	// Resolve the error copy/links from the template, mirroring inline chat's ErrorView.
	const { errorText, canRetry } = useMemo((): {
		canRetry: boolean;
		errorText: ReactNode;
	} => {
		switch (thinkingError?.template) {
			case 'RATE_LIMIT':
				return {
					errorText: formatMessage(errorViewMessages.rateLimited, {
						a: (chunks: ReactNode[]) => (
							<Anchor
								href="https://support.atlassian.com/organization-administration/docs/excessive-use-in-atlassian-intelligence/"
								target="_blank"
							>
								{chunks}
							</Anchor>
						),
					}),
					canRetry: false,
				};
			case 'ACCEPTABLE_USE_VIOLATIONS':
				return {
					errorText: formatMessage(errorViewMessages.aupViolation, {
						a: (chunks: ReactNode[]) => (
							<Anchor href="https://www.atlassian.com/legal/acceptable-use-policy" target="_blank">
								{chunks}
							</Anchor>
						),
					}),
					canRetry: false,
				};
			case 'TOKEN_LIMIT_EXCEEDED':
				return {
					errorText: formatMessage(aiSuggestionsMessages.thinkingErrorTokenLimitExceeded),
					canRetry: true,
				};
			default:
				return {
					errorText: formatMessage(errorViewMessages.unknownError),
					canRetry: true,
				};
		}
	}, [thinkingError, formatMessage]);

	const thinkingErrorProps = useMemo(
		() => ({
			onClose: handleClose,
			// Hide "Try again" for non-retryable errors (rate limit, AUP).
			onTryAgain: canRetry ? handleTryAgain : undefined,
			errorText,
		}),
		[handleClose, handleTryAgain, canRetry, errorText],
	);

	if (thinkingStatus === 'idle' || thinkingStatus === undefined) {
		return null;
	}

	// Errors bypass the isRovoMounted hide so they show even when Rovo chat is open.
	const isContainerHidden = isError ? isHidden : isRovoMounted || isHidden;

	return (
		<div
			css={[
				styles.container,
				fg('rovo_ready_bar_ui_update') && styles.resizableContainer,
				isContainerHidden && styles.hidden,
			]}
		>
			<RovoThinkingMinimization ref={minimizationRef} isThinking={thinkingStatus === 'thinking'}>
				<RovoThinkingBarWrapper isThinking={thinkingStatus === 'thinking'}>
					<RovoThinkingReadyTransition
						isReady={isReady}
						autoClose={!hasSuggestions}
						autoCloseDelay={AUTO_CLOSE_DELAY_MS}
						resizeToReadyContent={fg('rovo_ready_bar_ui_update')}
						thinkingBarProps={thinkingBarProps}
						thinkingReadyProps={thinkingReadyProps}
						isError={isError}
						thinkingErrorProps={thinkingErrorProps}
					/>
				</RovoThinkingBarWrapper>
			</RovoThinkingMinimization>
		</div>
	);
}
